/**
 * Game Engine API Client
 * Connects to the nt3d-game-engine worker for:
 * - Player Progression (XP, levels, titles, achievements)
 * - Faction Seasons (multi-round competition)
 * - THALIA Side Missions (daily/weekly challenges)
 * - Mini-Pots (side prize pools)
 * - THALIA Bounties (urgent timed challenges)
 * - Dynamic Story Events (THALIA-driven narrative)
 */

const GAME_ENGINE_URL = import.meta.env.VITE_GAME_ENGINE_URL || 'https://nt3d-game-engine.wurkit.workers.dev';

// ============================================================================
// TYPES (matching worker types)
// ============================================================================

export interface PlayerProfile {
  address: string;
  xp: number;
  level: number;
  title: string;
  totalKeysBought: number;
  totalPAWSpent: number;
  roundsPlayed: number;
  roundsWon: number;
  missionsCompleted: number;
  bountiesClaimed: number;
  currentStreak: number;
  longestStreak: number;
  factionHistory: Record<string, number>;
  achievements: string[];
  storyEventsTriggered: number;
  lastActive: number;
  seasonPoints: number;
  miniPotWins: number;
  // Extra fields from API
  xpForNextLevel?: number;
  xpProgress?: number;
  xpNeeded?: number;
  progressPercent?: number;
  allAchievements?: Achievement[];
}

export interface Achievement {
  id: string;
  name: string;
  desc: string;
  xp: number;
  unlocked: boolean;
}

export interface FactionSeason {
  seasonId: number;
  startRound: number;
  endRound: number;
  startTime: number;
  endTime: number;
  seasonEndDate: number;
  factionRanking: FactionSeasonScore[];
  topPlayers: SeasonLeaderboardEntry[];
  isActive: boolean;
  theme: string;
  thaliaCommentary: string;
  roundsRemaining: number;
  daysRemaining?: number;
}

export interface FactionSeasonScore {
  factionName: string;
  totalKeys: number;
  totalMembers: number;
  roundsWon: number;
  missionsCompleted: number;
  bountiesClaimed: number;
  score: number;
  rank: number;
}

export interface SeasonLeaderboardEntry {
  address: string;
  faction: string;
  score: number;
  rank: number;
}

export interface Mission {
  id: string;
  type: 'daily' | 'weekly' | 'special';
  title: string;
  description: string;
  thaliaFlavorText: string;
  objective: {
    type: string;
    target: number;
  };
  xpReward: number;
  bonusReward?: string;
  expiresAt: number;
  createdAt: number;
  difficulty: 'easy' | 'medium' | 'hard' | 'legendary';
  factionBonus?: string;
  // Progress (when fetched with player progress)
  progress?: number;
  completed?: boolean;
  claimed?: boolean;
}

export interface MiniPot {
  id: string;
  name: string;
  type: string;
  description: string;
  thaliaQuote: string;
  pool: number;
  roundId: number;
  entries: { address: string; score: number; timestamp: number }[];
  winner?: string;
  isActive: boolean;
  criteria: string;
}

export interface Bounty {
  id: string;
  title: string;
  description: string;
  thaliaChallenge: string;
  type: string;
  target: number;
  currentBest?: { address: string; score: number };
  xpReward: number;
  expiresAt: number;
  createdAt: number;
  isUrgent: boolean;
  claimedBy?: string;
  status: 'active' | 'claimed' | 'expired';
  timeRemaining?: number;
  timeRemainingFormatted?: string;
}

export type StoryEventType =
  | 'pot_milestone'
  | 'faction_shift'
  | 'player_achievement'
  | 'round_pattern'
  | 'tension_spike'
  | 'lull_taunt'
  | 'blood_moon_prophecy'
  | 'season_arc'
  | 'whale_narrative'
  | 'underdog_story'
  | 'rivalry_detected'
  | 'extinction_aftermath';

export interface StoryEvent {
  id: string;
  type: StoryEventType;
  title: string;
  narrative: string;
  triggerData: Record<string, unknown>;
  roundId: number;
  timestamp: number;
  reactions: number;
  chapter: number;
  arcId: string;
}

export interface StoryArc {
  arc: StoryEvent[];
}

export interface EventReportResult {
  xpEarned: number;
  totalXP: number;
  level: number;
  title: string;
  levelUp: boolean;
  newAchievements: string[];
  missionsCompleted: { title: string; xpReward: number; thaliaFlavorText: string }[];
  storyEvent: { title: string; narrative: string; type: StoryEventType } | null;
}

// ============================================================================
// API CLIENT
// ============================================================================

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${GAME_ENGINE_URL}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  if (!response.ok) throw new Error(`API error: ${response.status}`);
  return response.json() as Promise<T>;
}

// #1 — Player Progression
export async function getPlayerProfile(address: string): Promise<PlayerProfile> {
  return apiFetch<PlayerProfile>(`/player/${address.toLowerCase()}`);
}

export async function getXPLeaderboard(): Promise<{ leaderboard: PlayerProfile[] }> {
  return apiFetch(`/leaderboard`);
}

export async function reportGameEvent(
  address: string,
  eventType: string,
  data: Record<string, unknown>
): Promise<EventReportResult> {
  return apiFetch('/player/event', {
    method: 'POST',
    body: JSON.stringify({ address, eventType, data }),
  });
}

export async function getAchievementsList(): Promise<{ achievements: Achievement[] }> {
  return apiFetch('/achievements');
}

export async function getLevelsList(): Promise<{ levels: { level: number; title: string; xpRequired: number }[] }> {
  return apiFetch('/levels');
}

// #2 — Faction Seasons
export async function getCurrentSeason(): Promise<FactionSeason> {
  return apiFetch('/season');
}

export async function getSeasonHistory(): Promise<{ seasons: FactionSeason[] }> {
  return apiFetch('/season/history');
}

// #3 — Missions
export async function getActiveMissions(): Promise<{ missions: Mission[] }> {
  return apiFetch('/missions');
}

export async function getPlayerMissionProgress(address: string): Promise<{ missions: Mission[] }> {
  return apiFetch(`/missions/progress/${address.toLowerCase()}`);
}

// #5 — Mini-Pots
export async function getMiniPots(roundId: number): Promise<{ pots: MiniPot[] }> {
  return apiFetch(`/minipots/${roundId}`);
}

// #8 — Bounties
export async function getActiveBounties(): Promise<{ bounties: Bounty[] }> {
  return apiFetch('/bounties');
}

export async function claimBounty(
  bountyId: string,
  address: string,
  score: number
): Promise<{ success: boolean; bounty?: Bounty; xpAwarded: number }> {
  return apiFetch('/bounties/claim', {
    method: 'POST',
    body: JSON.stringify({ bountyId, address, score }),
  });
}

// #17 — Dynamic Story Events
export async function getStoryEvents(limit = 20): Promise<{ events: StoryEvent[] }> {
  return apiFetch(`/story?limit=${limit}`);
}

export async function getLatestStoryEvents(count = 5): Promise<{ events: StoryEvent[] }> {
  return apiFetch(`/story/latest?count=${count}`);
}

export async function getStoryArc(arcId: string): Promise<StoryArc> {
  return apiFetch(`/story/arc/${arcId}`);
}

export async function evaluateStoryTriggers(
  gameState: Record<string, unknown>
): Promise<{ triggered: boolean; event?: StoryEvent }> {
  return apiFetch('/story/evaluate', {
    method: 'POST',
    body: JSON.stringify(gameState),
  });
}

export async function reactToStoryEvent(
  eventId: string,
  _reaction?: string
): Promise<{ success: boolean }> {
  return apiFetch('/story/react', {
    method: 'POST',
    body: JSON.stringify({ eventId }),
  });
}

// ============================================================================
// REACT HOOK HELPER — Report events automatically on key purchase
// ============================================================================

export function createGameEngineReporter(address: string) {
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  return {
    /** Report a key purchase to the game engine */
    reportPurchase(data: {
      keys: number;
      pawSpent: number;
      factionName: string;
      roundId: number;
      pot?: number;
      potUsd?: number;
      pawPrice?: number;
      totalKeys?: number;
      uniquePlayers?: number;
      comboCount?: number;
      isAssassinMode?: boolean;
      isBloodMoon?: boolean;
      isFlashCrash?: boolean;
      timeSinceRoundStart?: number;
      timeRemaining?: number;
    }) {
      // Debounce to avoid spamming
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        reportGameEvent(address, 'key_purchase', data).catch(console.error);
      }, 500);
    },

    /** Report round participation */
    reportRoundParticipation(roundId: number, factionName: string) {
      reportGameEvent(address, 'round_participated', { roundId, factionName }).catch(console.error);
    },

    /** Report a round win */
    reportRoundWin(roundId: number, factionName: string) {
      reportGameEvent(address, 'round_won', { roundId, factionName }).catch(console.error);
    },

    /** Report a combo achievement */
    reportCombo(comboLevel: number) {
      reportGameEvent(address, 'combo_achieved', { comboLevel }).catch(console.error);
    },

    /** Report a reaper key purchase */
    reportReaperKey() {
      reportGameEvent(address, 'reaper_key', {}).catch(console.error);
    },

    /** Report a referral */
    reportReferral() {
      reportGameEvent(address, 'referral', {}).catch(console.error);
    },
  };
}

// Difficulty colors for mission display
export const DIFFICULTY_COLORS = {
  easy: { bg: 'bg-green-900/30', text: 'text-green-400', border: 'border-green-500/30' },
  medium: { bg: 'bg-yellow-900/30', text: 'text-yellow-400', border: 'border-yellow-500/30' },
  hard: { bg: 'bg-red-900/30', text: 'text-red-400', border: 'border-red-500/30' },
  legendary: { bg: 'bg-purple-900/30', text: 'text-purple-400', border: 'border-purple-500/30' },
};
